import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

// Orders that were once at HC - Back Order (i.e. have a blocked_order_notes
// entry with stage_at_note = 'HC - Back Order') but whose current stage is
// no longer that -- i.e. they've moved on and aren't being worked today.
const BackOrderHistory = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showDispatched, setShowDispatched] = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_backorder_history');
    if (error) {
      console.error('get_backorder_history error:', error);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);

  const term = search.trim().toLowerCase();
  const filtered = rows
    .filter((r) => showDispatched || r.stage !== 'Dispatched')
    .filter((r) => !term || (r.order_ref || '').toLowerCase().includes(term));

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/')}>← Home</button>
        <span className="page-nav__title">Back Order History</span>
        <button className="back-btn" onClick={() => navigate('/backorder-management')}>📥 Back Order Management</button>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">
          <SectionHeader
            title="Previously Back Ordered"
            count={filtered.length}
            onRefresh={load}
            loading={loading}
            actions={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search order… e.g. OJUN84407-33"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    background: 'var(--bg3)', color: 'var(--white)',
                    border: '1px solid var(--border2)', borderRadius: 4,
                    padding: '4px 10px', fontFamily: 'var(--font-body)',
                    fontSize: 12, width: 230,
                  }}
                />
                {search && (
                  <button
                    className="action-btn action-btn--ghost"
                    onClick={() => setSearch('')}
                    style={{ fontSize: 12 }}
                  >
                    Clear
                  </button>
                )}
                <button
                  className={`action-btn ${showDispatched ? 'action-btn--active' : 'action-btn--ghost'}`}
                  onClick={() => setShowDispatched((v) => !v)}
                  style={{ fontSize: 12 }}
                >
                  {showDispatched ? 'Showing Dispatched ✓' : 'Show Dispatched'}
                </button>
              </div>
            }
          />
          <p className="section__desc" style={{ padding: '0 16px' }}>
            Orders that have a note logged against them while at HC - Back Order (so appeared on
            Back Order Management at some point) but are no longer at that stage — newest first.
            {!showDispatched && ' Dispatched orders are hidden by default.'}
          </p>

          {loading ? <div className="dash-loading">Loading…</div> : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Stage</th>
                  <th>Order Date</th>
                  <th>Last Note</th>
                  <th>Last Note Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.order_id}>
                    <td><span className="code-badge">{row.order_ref}</span></td>
                    <td>{row.status || '—'}</td>
                    <td>{row.stage || '—'}</td>
                    <td className="muted">{fmtDate(row.created_date)}</td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                      {row.last_note_initials && (
                        <span style={{ color: 'var(--gold)', marginRight: 6 }}>[{row.last_note_initials}]</span>
                      )}
                      {row.last_note || '—'}
                    </td>
                    <td className="muted">{fmtDate(row.last_note_at)}</td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={6} className="dash-empty">
                      {term ? `No orders matching "${search.trim()}"` : 'No resolved back orders'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

export default BackOrderHistory;
